import * as RadixSelect from '@radix-ui/react-select';
import { forwardRef, memo, type ReactNode } from 'react';
import { classNames } from '~/utils/classNames';

type Option = {
  label: ReactNode;
  value: string;
};

type SelectItemProps = RadixSelect.SelectItemProps & {
  className?: string;
  children: ReactNode;
};

const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(({ children, className, ...props }, forwardedRef) => {
  return (
    <RadixSelect.Item
      className={classNames(
        'group',
        'relative flex cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm outline-none',
        'text-bolt-elements-textPrimary text-sm',
        'data-[highlighted]:bg-bolt-ds-surface-highlight',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        'data-[state=checked]:font-semibold',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
    </RadixSelect.Item>
  );
});

interface SelectProps {
  id?: string;
  className?: string;
  labelClassName?: string;
  contentClassName?: string;
  containerClassName?: string;
  size?: 'lg' | 'md';
  disabled?: boolean;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  value?: string;
  options: Option[];
  placeholder?: string;
  helper?: ReactNode;
  error?: ReactNode;
  label?: ReactNode;
  labelPosition?: 'top' | 'side';
}

export const Select = memo<SelectProps>(
  ({
    id,
    className,
    labelClassName,
    contentClassName,
    containerClassName,
    disabled,
    size = 'lg',
    onValueChange,
    defaultValue,
    value,
    options,
    placeholder = 'Select…',
    helper,
    error,
    label,
    labelPosition = 'top',
  }) => {
    const hasError = Boolean(error);

    return (
      <div
        className={classNames(
          'w-full',
          labelPosition === 'side' ? 'flex items-baseline gap-4' : 'flex flex-col gap-1.5',
          containerClassName,
        )}
      >
        {label && (
          <label
            htmlFor={id}
            className={classNames(
              'text-sm font-medium text-bolt-elements-textSecondary',
              disabled ? 'opacity-50 pointer-events-none' : '',
              labelClassName,
            )}
          >
            {label}
          </label>
        )}

        <div className="flex flex-col gap-1.5 w-full">
          <RadixSelect.Root defaultValue={defaultValue} value={value} onValueChange={onValueChange} disabled={disabled}>
            <RadixSelect.Trigger
              style={{ outline: 'none' }}
              className={classNames(
                'flex items-center justify-between w-full px-3 py-2 rounded-md text-sm shadow-sm',
                'bg-bolt-ds-surfaceOne data-[placeholder]:text-bolt-elements-textTertiary border rounded-md focus:ring-2 focus:border-transparent',
                size === 'lg' ? 'h-10' : size === 'md' ? 'h-9' : '',
                hasError
                  ? 'border-bolt-ds-danger focus:ring-bolt-ds-danger'
                  : 'border-bolt-ds-surface-highlight focus:ring-bolt-elements-borderColorActive',
                className,
              )}
            >
              <RadixSelect.Value placeholder={placeholder} className="text-sm text-bolt-elements-textPrimary" />
              <RadixSelect.Icon className="ml-2 text-bolt-elements-textTertiary">
                <div className="i-bolt:chevron-down text-xl" />
              </RadixSelect.Icon>
            </RadixSelect.Trigger>

            <RadixSelect.Portal>
              <RadixSelect.Content
                sideOffset={4}
                position="popper"
                className={classNames(
                  contentClassName,
                  'w-[var(--radix-select-trigger-width)] max-h-[var(--radix-select-content-available-height)] rounded-md border border-bolt-ds-surface-highlight bg-bolt-ds-surfaceOne shadow-lg animate-in fade-in z-max',
                )}
              >
                <RadixSelect.ScrollUpButton className="flex items-center justify-center h-6 text-bolt-elements-textSecondary">
                  <div className="i-bolt:chevron-up text-xl" />
                </RadixSelect.ScrollUpButton>
                <RadixSelect.Viewport className="p-1">
                  <RadixSelect.Group>
                    {options.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </RadixSelect.Group>
                </RadixSelect.Viewport>
                <RadixSelect.ScrollDownButton className="flex items-center justify-center h-6 text-bolt-elements-textTertiary">
                  <div className="i-bolt:chevron-down text-xl" />
                </RadixSelect.ScrollDownButton>
              </RadixSelect.Content>
            </RadixSelect.Portal>
          </RadixSelect.Root>

          {helper && !error && <div className="text-xs text-bolt-elements-textTertiary">{helper}</div>}
          {error && <div className="text-xs text-bolt-ds-danger">{error}</div>}
        </div>
      </div>
    );
  },
);
